/** Lightweight Web Audio SFX — no asset downloads required. */
export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = false;

  private ensure(): AudioContext | null {
    if (this.muted) return null;
    if (!this.ctx) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.35;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  private tone(
    freq: number,
    dur: number,
    type: OscillatorType = 'sine',
    gain = 0.2,
    freqEnd?: number,
  ): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (freqEnd != null) osc.frequency.exponentialRampToValueAtTime(Math.max(20, freqEnd), t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private noise(dur: number, gain = 0.12): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    const n = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, n, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < n; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / n);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = gain;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1800;
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    src.start();
  }

  unlock(): void {
    this.ensure();
  }

  enGarde(): void {
    this.tone(220, 0.08, 'triangle', 0.12);
    setTimeout(() => this.tone(330, 0.1, 'triangle', 0.1), 90);
  }

  allez(): void {
    this.tone(440, 0.12, 'square', 0.08);
    setTimeout(() => this.tone(660, 0.18, 'square', 0.1), 100);
  }

  footstep(): void {
    this.noise(0.04, 0.04);
    this.tone(80, 0.05, 'sine', 0.04);
  }

  lunge(): void {
    this.noise(0.08, 0.08);
    this.tone(140, 0.15, 'sawtooth', 0.06, 60);
  }

  parry(): void {
    this.tone(900, 0.05, 'square', 0.1);
    this.tone(1200, 0.08, 'triangle', 0.08);
    this.noise(0.05, 0.06);
  }

  touchValid(player: boolean): void {
    this.tone(player ? 523 : 392, 0.25, 'square', 0.14);
    setTimeout(() => this.tone(player ? 659 : 330, 0.35, 'square', 0.12), 80);
    this.noise(0.12, 0.1);
  }

  touchOff(): void {
    this.tone(200, 0.2, 'triangle', 0.1);
    this.noise(0.1, 0.08);
  }

  halt(): void {
    this.tone(180, 0.15, 'sine', 0.08);
  }

  win(): void {
    [523, 659, 784, 1046].forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.2, 'triangle', 0.12), i * 120);
    });
  }

  lose(): void {
    this.tone(300, 0.3, 'sawtooth', 0.1, 100);
  }

  uiClick(): void {
    this.tone(700, 0.04, 'sine', 0.06);
  }

  cameraSwitch(): void {
    this.tone(500, 0.05, 'triangle', 0.05);
    this.tone(800, 0.06, 'triangle', 0.04);
  }
}
